import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type DnaEra,
  type DnaFacetRecord,
  type DnaPhase,
  type DnaProfileRecord,
  type DnaSessionRecord,
  type DnaTurnResult,
  type InputCapture,
  dnaTurn as apiDnaTurn,
  createDnaSession,
  getCurrentDnaSession,
  synthesiseDna,
} from './api';

// =============================================================================
// useDna — the turn-based excavation orchestration hook (LANE DNA-BUILD,
// Phase 1). Ported VERBATIM from apps/web/src/lib/useDna.ts: it is pure
// orchestration (resume/start a session, submit an answer, run synthesis),
// zero JSX, zero apps/web-specific imports beyond its own api.ts functions —
// a clean lift per the plan's own read of this file
// (docs/design/dna-interview-port-2026-07-05.md §3 Phase 1). TTS playback +
// audio capture stay in the page/component layer, not here, mirroring
// apps/web's split exactly.
// =============================================================================

export interface DnaState {
  session: DnaSessionRecord | null;
  facets: DnaFacetRecord[];
  profile: DnaProfileRecord | null;
  eras: DnaEra[];
  currentQuestion: string;
  phase: DnaPhase;
  loading: boolean;
  busy: boolean;
  error: string | null;
  goldSpotted: boolean;
}

/** The turn-state-machine's own resume decision: given a resumed session's
 *  append-only transcript, what question is currently "live" on screen? The
 *  last assistant turn — walked backward so a resume never re-surfaces a
 *  stale question the user already answered. Exported (not just used
 *  inline) so it is directly unit-pinned in useDna.test.ts, same house
 *  pattern as every other pure decision function in lib/ (onboardingStep.ts,
 *  refineThreadsSource.ts, etc.) — the one piece of real state-derivation
 *  logic in this hook that does not require a DOM/React-Testing-Library
 *  harness (absent from this repo) to verify. */
export function lastQuestionOf(session: DnaSessionRecord | null): string {
  const t = session?.transcript ?? [];
  for (let i = t.length - 1; i >= 0; i--) if (t[i].role === 'assistant') return t[i].text;
  return '';
}

/** Orchestrates the excavation: resume/start a session, submit answers (which
 *  distil facets + ask the next question), and synthesise the DNA at the end.
 *  Voice capture lives in the page (useVoiceAnswer). TTS question playback is
 *  Phase 3 (docs/design/dna-interview-port-2026-07-05.md) — deferred; the
 *  question renders as visible text here, same as apps/web's own visual
 *  reader path. */
export function useDna() {
  const [state, setState] = useState<DnaState>({
    session: null,
    facets: [],
    profile: null,
    eras: [],
    currentQuestion: '',
    phase: 'origins',
    loading: true,
    busy: false,
    error: null,
    goldSpotted: false,
  });
  const sessionIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const cur = await getCurrentDnaSession();
      sessionIdRef.current = cur.session?.id ?? null;
      setState((s) => ({
        ...s,
        session: cur.session,
        facets: cur.facets,
        profile: cur.profile,
        eras: cur.eras ?? [],
        currentQuestion: lastQuestionOf(cur.session),
        phase: cur.session?.phase ?? 'origins',
        loading: false,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const start = useCallback(async (): Promise<string | null> => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const r = await createDnaSession();
      sessionIdRef.current = r.session.id;
      setState((s) => ({
        ...s,
        session: r.session,
        eras: r.eras,
        facets: [],
        profile: null,
        currentQuestion: r.firstQuestion,
        phase: 'origins',
        busy: false,
      }));
      return r.firstQuestion;
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: false,
        error: err instanceof Error ? err.message : String(err),
      }));
      return null;
    }
  }, []);

  const submit = useCallback(async (capture: InputCapture): Promise<DnaTurnResult | null> => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return null;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const r = await apiDnaTurn(sessionId, capture);
      setState((s) => ({
        ...s,
        currentQuestion: r.nextQuestion,
        facets: [...s.facets, ...r.facets],
        phase: r.phase,
        goldSpotted: r.goldSpotted,
        busy: false,
      }));
      return r;
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: false,
        error: err instanceof Error ? err.message : String(err),
      }));
      return null;
    }
  }, []);

  const runSynthesis = useCallback(async (): Promise<DnaProfileRecord | null> => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return null;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const r = await synthesiseDna(sessionId);
      setState((s) => ({ ...s, profile: r.profile, busy: false }));
      return r.profile;
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: false,
        error: err instanceof Error ? err.message : String(err),
      }));
      return null;
    }
  }, []);

  return { state, reload: load, start, submit, runSynthesis };
}
